import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Typography,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Box,
  useTheme,
  useMediaQuery,
  Button,
  Card,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Logout as LogoutIcon,
  Dashboard as DashboardIcon,
  Assessment as SalesIcon,
  AccountBalance as CommissionIcon,
  Timeline as TrendsIcon,
  People as PeopleIcon,
  Business as BusinessIcon,
  ShoppingCart as ShoppingCartIcon,
  TrendingUp as TrendingUpIcon,
  Inventory2 as InventoryIcon,
  SwapHoriz as TransferIcon,
  Folder as FolderIcon,
} from '@mui/icons-material';

const drawerWidth = 240;

const RepNavigation = ({ children, currentUser, onLogout }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLogout = () => {
    onLogout();
  };

  // Determine base path dynamically based on current location
  const getBasePath = () => {
    const currentPath = location.pathname;
    return currentPath.startsWith('/client_rep') ? '/client_rep' : '/rep';
  };

  const basePath = getBasePath();

  const menuItems = [
    { text: 'Overview', icon: <DashboardIcon />, path: `${basePath}/dashboard` },
    { text: 'My Clients', icon: <PeopleIcon />, path: `${basePath}/clients` },
    { text: 'CRM / Leads', icon: <BusinessIcon />, path: `${basePath}/crm-leads` },
    { text: 'Orders ($)', icon: <ShoppingCartIcon />, path: `${basePath}/orders` },
    { text: 'Transfers (Free)', icon: <TransferIcon />, path: `${basePath}/transfers` },
    // { text: 'Promo Codes', icon: <TrendingUpIcon />, path: `${basePath}/promo-codes` }, // Hidden for now
    { text: 'Analytics', icon: <TrendingUpIcon />, path: `${basePath}/analytics` },
    { text: 'Resources', icon: <FolderIcon />, path: `${basePath}/resources` },
    { text: 'Inventory', icon: <InventoryIcon />, path: `${basePath}/inventory` },
  ];

  const accountItems = [
    { text: 'Sign Out', icon: <LogoutIcon />, action: handleLogout }
  ];

  const drawer = (
    <Box sx={{
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      bgcolor: '#f7f7f8',
      borderRight: '1px solid #ececef',
      fontFamily: 'Poppins, sans-serif'
    }}>
      {/* Top Banner */}
      <Box sx={{ p: 2, pt: 0, mb: 2 }}>
        <Box
          sx={{
            mx: -2,
            width: 'auto',
            height: 56,
            bgcolor: '#D9D9D9',
            borderBottom: '1px solid #ececef',
            borderTopLeftRadius: 0,
            borderTopRightRadius: 0
          }}
        />
      </Box>

      {/* Navigation Menu */}
      <Box sx={{ px: 2, flexGrow: 1 }}>
        <List sx={{ p: 0 }}>
          {menuItems.map((item) => {
            const isActive = location.pathname === item.path;
            
            return (
              <ListItem key={item.text} disablePadding sx={{ mb: '4px' }}>
                <ListItemButton
                  onClick={() => {
                    navigate(item.path);
                    if (isMobile) setMobileOpen(false);
                  }}
                  sx={{
                    borderRadius: '6px',
                    backgroundColor: isActive
                      ? '#e5e7eb'
                      : 'transparent',
                    color: '#212121',
                    '&:hover': {
                      backgroundColor: isActive ? '#e5e7eb' : '#f7f7f8'
                    },
                    py: '8px',
                    px: '8px',
                    gap: '4px',
                    minHeight: '36px'
                  }}
                >
                  <ListItemIcon
                    sx={{
                      color: '#475569',
                      minWidth: 'auto',
                      mr: '8px',
                      '& .MuiSvgIcon-root': {
                        fontSize: isActive ? 24 : 18,
                        color: '#475569'
                      }
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.text}
                    primaryTypographyProps={{
                      fontFamily: 'Poppins',
                      fontSize: '15px',
                      fontWeight: 400,
                      lineHeight: '20px',
                      color: '#475569',
                    }}
                  />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      </Box>

      {/* Help Section */}
      <Box sx={{ p: 2 }}>
        <Card sx={{
          bgcolor: 'white',
          borderRadius: '6px',
          border: '1px solid #ececef',
          boxShadow: 'none',
          p: 2
        }}>
          <Box sx={{ mb: 2 }}>
            <Typography sx={{
              fontFamily: 'Poppins',
              fontSize: '15px',
              fontWeight: 700,
              color: '#212121',
              lineHeight: '20px',
              mb: '4px'
            }}>
              Need Help?
            </Typography>
            <Typography sx={{
              fontFamily: 'Poppins',
              fontSize: '12px',
              fontWeight: 400,
              color: '#212121',
              lineHeight: '18px'
            }}>
              Can't find something? We are always here to help.
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: '8px' }}>
            <Button
              variant="outlined"
              onClick={() => window.open('https://help.lipoderma.com', '_blank')}
              sx={{
                fontFamily: 'Poppins',
                fontSize: '12px',
                fontWeight: 500,
                color: '#212121',
                borderColor: '#ececef',
                borderRadius: '6px',
                textTransform: 'none',
                py: '6px',
                px: '12px',
                bgcolor: 'white',
                '&:hover': {
                  borderColor: '#ececef',
                  bgcolor: 'white',
                  opacity: 0.8
                }
              }}
            >
              Help Center
            </Button>
            <Button
              onClick={() => window.open('mailto:support@lipoderma.com')}
              sx={{
                fontFamily: 'Poppins',
                fontSize: '12px',
                fontWeight: 500,
                color: '#212121',
                borderRadius: '6px',
                textTransform: 'none',
                py: '6px',
                px: '4px',
                minWidth: 'auto',
                '&:hover': {
                  bgcolor: 'transparent',
                  opacity: 0.8
                }
              }}
            >
              Contact Us
            </Button>
          </Box>
        </Card>
      </Box>

      {/* Account Actions at the very bottom */}
      <Box sx={{ p: 2 }}>
        <List sx={{ p: 0 }}>
          {accountItems.map((item) => (
            <ListItem key={item.text} disablePadding sx={{ mb: '4px' }}>
              <ListItemButton
                onClick={item.action}
                sx={{
                  borderRadius: '6px',
                  color: '#dc2626',
                  '&:hover': {
                    backgroundColor: '#fee2e2'
                  },
                  py: '8px',
                  px: '8px',
                  gap: '4px',
                  minHeight: '36px'
                }}
              >
                <ListItemIcon
                  sx={{
                    color: '#dc2626',
                    minWidth: 'auto',
                    mr: '4px',
                    '& .MuiSvgIcon-root': {
                      fontSize: 18
                    }
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.text}
                  primaryTypographyProps={{
                    fontFamily: 'Poppins',
                    fontSize: '15px',
                    fontWeight: 500,
                    lineHeight: '20px',
                    color: '#dc2626'
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* Navigation Drawer */}
      <Box component="nav" sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}>
        {/* Mobile drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              borderTopLeftRadius: 0,
              borderTopRightRadius: 0
            }
          }}
        >
          {drawer}
        </Drawer>

        {/* Desktop drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              position: 'fixed',
              top: 0,
              left: 0,
              height: '100vh',
              zIndex: 1200,
              borderTopLeftRadius: 0,
              borderTopRightRadius: 0
            }
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Mobile Menu Button */}
      <IconButton
        color="inherit"
        aria-label="open drawer"
        edge="start"
        onClick={handleDrawerToggle}
        sx={{
          position: 'fixed',
          top: 20,
          left: 20,
          zIndex: 1300,
          display: { md: 'none' },
          bgcolor: '#039582',
          color: 'white',
          '&:hover': { bgcolor: '#027A6B' }
        }}
      >
        <MenuIcon />
      </IconButton>

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          backgroundColor: theme.palette.background.default,
          minHeight: '100vh'
        }}
      >
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      </Box>
    </Box>
  );
};

export default RepNavigation;
